import { Check, Copy, Mail, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';
import type { Invite } from '../lib/types';

/**
 * What happened after you handed somebody an account.
 *
 * ## Why this exists at all
 *
 * Creating a user used to close its form and say nothing. The account was
 * made, an email was queued, and the admin was left with no idea whether the
 * person could actually get in — so the honest options were to phone them and
 * read out a temporary password, or wait and hope. Both are worse than showing
 * the link.
 *
 * ## Why the link is on screen even when the email is queued
 *
 * In an office of nine people the new starter is usually standing next to you.
 * Reading a link off the screen is faster than waiting for an inbox, and it is
 * the only thing that works on the day the mail server is not.
 *
 * ## Why it does not claim the email was sent
 *
 * Because nothing here knows that. The API puts the message in an outbox and a
 * worker delivers it later; the response can only say it was accepted for
 * delivery. Saying "sent" over a message that later failed is exactly how
 * somebody waits three days for a welcome that never left — and this console
 * did say it, until it was corrected.
 */
export function InviteResult({
  title,
  person,
  invite,
  password,
  onClose,
}: {
  title: string;
  /** Who it is for — shown so a mistyped address is caught here, not later. */
  person: { name: string; email: string };
  invite: Invite;
  /**
   * The temporary password the admin typed, if there was one.
   *
   * Shown second and quietly. It still works, so hiding it would be a lie, but
   * the link is the better route by every measure: it is single-use, it
   * expires, and it does not require reading a secret down a phone to somebody
   * who will then type it wrongly.
   */
  password?: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!invite.url) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /*
       * Clipboard access is refused in plenty of ordinary situations — an
       * insecure origin, a permission the browser never granted. The link is
       * already on screen and selectable, so there is nothing to recover from
       * and nothing worth interrupting anybody about.
       */
    }
  };

  return (
    <Modal
      title={title}
      subtitle={`${person.name} · ${person.email}`}
      onClose={onClose}
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="space-y-4">
        {invite.emailQueued ? (
          <p className="flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-3 text-sm">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-navy-700" aria-hidden="true" />
            <span>
              An invitation is queued for <strong>{person.email}</strong>. It normally arrives
              within a minute or two. If it does not, the link below does the same job — check
              Settings → System status to see whether email is getting out.
            </span>
          </p>
        ) : (
          <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
            <span>
              <strong>No email could be queued.</strong> The account exists and works — but nothing
              is going to reach them on its own, so send the link below yourself.
            </span>
          </p>
        )}

        {invite.url ? (
          <div>
            <p className="text-sm font-medium">Their link</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              It sets their password, works once, and expires in {invite.expiresInHours} hours.
              Anybody who has it can get into this account, so send it the way you would send a
              password.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/*
                A read-only input rather than a <code> block: it is selectable
                with a tap and a long-press on a phone, where selecting text
                inside a paragraph is genuinely difficult.
              */}
              <input
                readOnly
                value={invite.url}
                aria-label="Invitation link"
                onFocus={(event) => event.target.select()}
                className="input min-w-0 flex-1 font-mono text-xs"
              />
              <button type="button" className="btn-secondary" onClick={copy}>
                {copied ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" aria-hidden="true" /> Copy
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No link could be issued. The account exists; use “Reset password” on their row to
            produce one.
          </p>
        )}

        {password ? (
          <details className="rounded-xl border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              If they cannot open the link
            </summary>
            <p className="mt-2 text-xs text-muted-foreground">
              The password you typed also works, once. It is shown here and nowhere else, and it
              stops working the moment they choose their own — which they are made to do before
              anything in the console will answer them.
            </p>
            <dl className="mt-2 space-y-1 rounded-lg bg-muted/40 p-2 font-mono text-xs">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-muted-foreground">email</dt>
                <dd className="font-medium">{person.email}</dd>
              </div>
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-muted-foreground">password</dt>
                <dd className="font-medium">{password}</dd>
              </div>
            </dl>
          </details>
        ) : null}

        <p className="text-xs text-muted-foreground">
          They choose their own password when they open it, then accept the terms and the privacy
          notice before anything else. Until they do, the account cannot be used.
        </p>
      </div>
    </Modal>
  );
}
